// #1784 — /academic/accept magic-link landing page.
//
// Consumes a token from the URL (?token=<hex>) and POSTs to
// /academic-invitations/accept. Mirrors SetupAcceptPage.tsx's structure —
// same problem shape (token-bearing magic link → backend POST → redirect),
// same solution. The invite email URL is already baked into
// academic-tenant.service.ts's buildInviteUrl as
// `${APP_URL}/academic/accept?token=...` — this page is what serves it.
//
// Backend contract (services/academic-tenant.service.ts's
// acceptAcademicInvitation + routes/academic-invitations.routes.ts),
// dispatched by ApiError.status — NOT by message-substring matching (R1+R2
// review: SetupAcceptPage.tsx's msg.includes(...) precedent is fragile, a
// stale-JWT 401 from authenticate contains "expired" and collides with the
// invalid-or-used branch's own substring check):
//   Success 200 → { tenantId, redirectTo } — page navigates to redirectTo.
//   400       → "Invitation is invalid, expired, or already used" (collapsed;
//                 backend deliberately does not distinguish expired / used /
//                 unknown to prevent enumeration).
//   401       → session-expired — the localStorage token exists but is no
//                 longer valid server-side; round-trips through /login same
//                 as the no-token case, NOT treated as an invitation problem.
//   403       → "This invitation was sent to a different email address"
//                 (renders both a Sign out CTA and a Create account CTA —
//                 #2712, see note below).
//   409       → "This account has a different role in this tenant and
//                 cannot accept a student invitation" (#1784 role-conflict
//                 ruling — a pre-existing non-STUDENT tenant member
//                 accepting a team-bearing invite).
//   other     → generic retry state.
//
// Safeguards mirrored from SetupAcceptPage.tsx (ONB.B2-FIX.D1 S1-S4):
//   S1: Unauthenticated branch URL-encodes /academic/accept?token=<token> as
//       the ?redirect= param; LoginPage/RegisterPage's redirect-honoring
//       (generic, already built) carries it back through the auth round trip.
//   S2: redirectTo comes from the parsed response — never hard-coded.
//       Defensive fallback to /dashboard if the response lacks it.
//   S3: Every successful-path navigate uses { replace: true } so the
//       browser back button does not return to the consumed magic link.
//   S4: Token is never written to console.log, analytics, or Sentry
//       contexts. Errors thrown by apiFetch carry only the backend's
//       error message (no token interpolation in messages).
//
// #2712 — the 403 (email-mismatch) branch is genuinely ambiguous about WHY
// the authenticated identity doesn't match the invitation: either (a) this
// browser has a stale/unrelated token (a previous unrelated login, another
// account's session left over from testing) and the real person behind this
// invite has never registered at all, or (b) the signed-in person is a real,
// existing user whose invited-address account is a different one than the
// one they're currently signed into. Auto-clearing the session and routing
// straight to registration would be wrong for (b) — it risks creating a
// duplicate account for someone who already has one. Rather than guess,
// both remedies are offered explicitly and the person self-selects: they
// know which case they're in, the code doesn't need to. "Create an account"
// reuses the exact same ?redirect= mechanism S1 already uses for the
// no-token case (same `here` construction, same target shape), not a
// second one.

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface AcceptResponse {
  tenantId: string;
  redirectTo?: string;
  // #1975 (S2) — a session re-scoped to the tenant just joined. The token the
  // user arrives with is tenant-less (the skip path mints it that way at
  // registration, because no membership existed yet), so without swapping this
  // in they would land in the dashboard with no tenant context — the defect
  // reported as "user remains in their personal tenant after accepting".
  token?: string;
}

// Mirrors SetupAcceptPage.tsx's State shape/reasoning — no separate
// 'success' variant; the loading UI continues to render until the navigate
// effect commits and the route unmounts this page.
//
// R1+R2 review (#1784) — classified by ApiError.status, NOT by
// message-substring matching. SetupAcceptPage.tsx's precedent used
// msg.includes(...), which both independent reviewers flagged as fragile:
// a stale JWT's 401 ("Invalid or expired token") contains the word
// "expired", which collided with the invalid-or-used branch's substring
// check and told the user their INVITATION had expired when really their
// SESSION had — the wrong remedy (asking the admin to resend vs. just
// signing back in). Status-code dispatch below is 1:1 with the backend's
// real error taxonomy (400/401/403/409) and has no substring collisions.
type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'invalid-or-used' }
  | { kind: 'email-mismatch' }
  | { kind: 'role-conflict' }
  | { kind: 'session-expired' }
  | { kind: 'server-error'; message: string };

export default function AcademicAcceptPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const authToken = localStorage.getItem('blueprint_token');
  const { setAuthFromToken } = useAuth();

  const [state, setState] = useState<State>({ kind: 'idle' });

  useEffect(() => {
    if (!token || !authToken) return;

    // No explicit setState('loading') here — 'idle' and 'loading' render
    // identically (see the shared branch below), so there's nothing to
    // transition into before the fetch settles. Deps are
    // [token, authToken, navigate] only, not state.kind — same reasoning as
    // SetupAcceptPage.tsx's R1 F2 fix.
    let cancelled = false;

    apiFetch<AcceptResponse>('/academic-invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then((data) => {
        if (cancelled) return;
        // #1975 (S2) — adopt the tenant-scoped session BEFORE navigating, so
        // the destination renders with the invitation's tenant already in
        // context rather than briefly with the tenant-less one.
        if (data.token) setAuthFromToken(data.token);
        const target =
          data.redirectTo && typeof data.redirectTo === 'string'
            ? data.redirectTo
            : '/dashboard';
        navigate(target, { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          switch (err.status) {
            case 401:
              setState({ kind: 'session-expired' });
              return;
            case 403:
              setState({ kind: 'email-mismatch' });
              return;
            case 409:
              setState({ kind: 'role-conflict' });
              return;
            case 400:
              setState({ kind: 'invalid-or-used' });
              return;
            default:
              setState({ kind: 'server-error', message: err.message });
              return;
          }
        }
        setState({ kind: 'server-error', message: err instanceof Error ? err.message : 'Failed to accept invitation' });
      });
    return () => {
      cancelled = true;
    };
  }, [token, authToken, navigate, setAuthFromToken]);

  const containerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', padding: 24,
    background: 'var(--bg)', fontFamily: 'var(--font-body)',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 440, width: '100%', padding: 32,
    borderRadius: 'var(--radius-xl)', background: 'var(--surface)',
    border: '1px solid var(--border)', textAlign: 'center',
  };

  const primaryButtonStyle: React.CSSProperties = {
    padding: '10px 24px', borderRadius: 'var(--radius-sm)',
    border: 'none', background: 'var(--primary)', color: '#fff',
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    padding: '10px 24px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
    fontWeight: 500, fontSize: 14, cursor: 'pointer',
  };

  // No token — defensive guard against direct visits without ?token.
  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            Missing invitation token
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            This page was opened without a valid invitation link.
          </p>
          <button onClick={() => navigate('/dashboard', { replace: true })} style={primaryButtonStyle}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Unauthenticated — redirect to /login carrying the full /academic/accept
  // URL+token as ?redirect=. LoginPage/RegisterPage read the param, validate
  // same-origin (isSafeRedirect), and navigate back here after auth.
  if (!authToken) {
    const here = `/academic/accept?token=${encodeURIComponent(token)}`;
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Sign in to accept
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            You need to sign in or create an account to accept this invitation.
          </p>
          <button
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(here)}`)}
            style={primaryButtonStyle}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ fontSize: 16, color: 'var(--text)' }}>Accepting invitation…</p>
        </div>
      </div>
    );
  }

  // Same remedy as the unauthenticated branch above (a stale localStorage
  // token is functionally the same problem as no token) — round-trip
  // through /login carrying this same page + token back as ?redirect=.
  if (state.kind === 'session-expired') {
    const here = `/academic/accept?token=${encodeURIComponent(token)}`;
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Session expired
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            Please sign in again to accept this invitation.
          </p>
          <button
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(here)}`)}
            style={primaryButtonStyle}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'invalid-or-used') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            This invitation is no longer valid
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            It may have expired or already been used. If you still need to accept an invitation, ask your tenant admin to send a new one.
          </p>
          <button onClick={() => navigate('/dashboard', { replace: true })} style={primaryButtonStyle}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'role-conflict') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            This account already has a role in this tenant
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            Your account already has a different role in this organisation, so it can&apos;t also accept a student invitation. Contact your tenant admin if you believe this is a mistake.
          </p>
          <button onClick={() => navigate('/dashboard', { replace: true })} style={primaryButtonStyle}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'email-mismatch') {
    const here = `/academic/accept?token=${encodeURIComponent(token)}`;
    const signOutAndRedirect = () => {
      localStorage.removeItem('blueprint_token');
      navigate(`/login?redirect=${encodeURIComponent(here)}`, { replace: true });
    };
    // #2712 — same ?redirect= shape S1's unauthenticated branch already uses
    // (see `here` above), so RegisterPage's existing invitation-flow handling
    // (skip-eligible token/type/email matching, then straight back to this
    // page) picks it up unchanged. Also clears the stale token: if it's
    // genuinely stale (case a), nothing of value is lost; if the person was
    // actually the right account after all, registration for their own
    // already-registered email will fail with "Email already registered" and
    // they still have the Sign out button as a way back to Login.
    const createAccountAndRedirect = () => {
      localStorage.removeItem('blueprint_token');
      navigate(`/register?redirect=${encodeURIComponent(here)}`, { replace: true });
    };
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            Wrong account
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            This invitation was sent to a different email address than the one you are signed in with.
            If that&apos;s your mistake, sign out and sign back in with the invited account. If
            you&apos;ve never registered with the invited email before, create an account with it instead.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={signOutAndRedirect} style={secondaryButtonStyle}>
              Sign out and try again
            </button>
            <button onClick={createAccountAndRedirect} style={primaryButtonStyle}>
              Create an account with the invited email
            </button>
          </div>
        </div>
      </div>
    );
  }

  // state.kind === 'server-error'
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
          Unable to accept invitation
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
          {state.message}
        </p>
        <button onClick={() => navigate('/dashboard', { replace: true })} style={{
          padding: '10px 24px', borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
          fontWeight: 500, fontSize: 14, cursor: 'pointer',
        }}>
          Go to dashboard
        </button>
      </div>
    </div>
  );
}
